import { AxiosInstance } from "axios";

import {
  AccountI,
  AccountStatusI,
  ContactDetailI,
} from "shared/lib/interfaces/account";
import {
  MessagingChatMember,
  MessagingChatMessageHistoricI,
} from "@/lib/types/messaging";
import { LastOnlineStatusI } from "shared/lib/interfaces/chat";

import { DefaultResponseI } from "shared/lib/interfaces/api";

export interface ChatInboxItemI {
  campaign_id: string;

  account?: AccountI;
  aud_id?: string;
  contact?: ContactDetailI;
  customer: {
    first_name: string;
    last_name: string;
    id: string;
    profile_pic_url: string;
  };
  last_message?: string;
  last_status_major?: AccountStatusI;
  last_status_minor?: AccountStatusI;
  notification_count: number;
  timestamp?: string;
}

export interface GetChatInboxResponseI {
  chat_inbox: Array<ChatInboxItemI>;
  next_token?: string;
}

export interface GetMessagingChatHistoryResponseI {
  customers_in_chat?: Array<any>;
  callers_in_chat?: Array<MessagingChatMember>;
  chat_history?: Array<MessagingChatMessageHistoricI>;
  next_token?: string;
}

export interface GetChatInboxOnlineStatusesResponseI extends DefaultResponseI {
  online_statuses: LastOnlineStatusI[];
}

export interface GetMessagesUnreadCountResponseI {
  message_count: number;
}

export interface GetChatInboxV2ParamsI {
  search_term?: string;
  next_token?: string | null;
}

export interface ChatInboxCustomerI {
  id: string;
  first_name: string;
  last_name: string;
  profile_pic_url: string;

  is_meeting_taker: boolean;
  is_default_owner: boolean;
}

export interface ChatInboxUserI {
  id: string;
  first_name: string;
  last_name: string;
  profile_pic_url: string;
}

export interface ChatInboxV2ItemI {
  campaign_id: string;
  customer: ChatInboxCustomerI;
  user: ChatInboxUserI;

  last_message: string;
  last_message_timestamp: string;
  notification_count: number;
}

export interface GetChatInboxV2ResponseI {
  chat_inbox: ChatInboxV2ItemI[];
  next_token?: string;
}

export interface CampaignCustomerI {
  customers: ChatInboxCustomerI[];
  campaign_name: string;
  campaign_id: string;
}

export interface GetCustomersToChatWithResponseI {
  campaign_customers: CampaignCustomerI[];
}

export interface GetCustomerToChatOnAccountDetailsResponseI {
  customer_id: string;
}

export const MessagingAPIEndpoints = (api: AxiosInstance) => ({
  getChatInbox: (
    nextToken: string | undefined,
    searchTerm: string | undefined
  ) =>
    api.post<GetChatInboxResponseI>("/v1/chat_inbox", {
      next_token: nextToken,
      search_term: searchTerm,
    }),

  getChatInboxV2: (params: GetChatInboxV2ParamsI) =>
    api.post<GetChatInboxV2ResponseI>(`/v2/chat_inbox`, params),

  getMessagingChatHistory: (
    audId: string,
    params: {
      next_token?: string;
    }
  ) =>
    api.post<GetMessagingChatHistoryResponseI>(
      `/v1/aud/${audId}/chat_history`,
      { ...params }
    ),

  getChatInboxOnlineStatuses: (userIds: string[]) =>
    api.post<GetChatInboxOnlineStatusesResponseI>("/v1/online_statuses", {
      ids: userIds,
    }),

  getMessagingChatHistoryV2: (
    campaignId: string,
    customerId: string,
    nextToken?: string | null
  ) =>
    api.post<GetMessagingChatHistoryResponseI>(
      `/v1/campaign/${campaignId}/customer/${customerId}/chat_history`,
      { next_token: nextToken }
    ),

  getCustomersToChatWith: () =>
    api.get<GetCustomersToChatWithResponseI>("/v1/available_customers_to_chat"),

  getCustomerToChatOnAccountDetails: (campaignId: string, accountId: string) =>
    api.get<GetCustomerToChatOnAccountDetailsResponseI>(
      `/v1/account/${accountId}/campaign/${campaignId}/customer`
    ),
});
